import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import Intercom, { boot, shutdown } from '@intercom/messenger-js-sdk';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

/** Mesmo workspace do Pay (`apps/pay/src/App.vue`). */
export const INTERCOM_APP_ID = 'eq4smcoy';
const INTERCOM_SCRIPT_ID = '_intercom_npm_loader';

export interface IntercomBootPayload {
  app_id: string;
  email: string;
  name?: string;
  user_id?: string;
  hide_default_launcher: boolean;
}

interface IntercomWatchState {
  isAuthenticated: boolean;
  shouldHideMessenger: boolean;
  email: string;
  name: string;
  userId: string;
}

function isAppEmbeddedInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function intercomWidgetLoaderExists(): boolean {
  return typeof document !== 'undefined' && Boolean(document.getElementById(INTERCOM_SCRIPT_ID));
}

function resolveIntercomEmail(personEmail?: string | null, accountEmail?: string): string {
  return (personEmail || accountEmail || '').trim();
}

function resolveIntercomUserId(sessionId?: string, accountId?: string): string {
  return (sessionId || accountId || '').trim();
}

export function buildIntercomPayload(params: {
  email: string;
  name: string;
  userId: string;
}): IntercomBootPayload {
  const name = params.name.trim();
  return {
    app_id: INTERCOM_APP_ID,
    email: params.email,
    name: name || undefined,
    user_id: params.userId || undefined,
    hide_default_launcher: false,
  };
}

function bootMessenger(payload: IntercomBootPayload): void {
  if (intercomWidgetLoaderExists()) {
    boot(payload);
    return;
  }
  Intercom(payload);
}

/**
 * Messenger Intercom no app autenticado (mesmo workspace do Pay).
 * Instanciar exatamente uma vez em App.vue.
 */
export function useIntercomMessenger(): void {
  const route = useRoute();
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const { isAuthenticated } = storeToRefs(authStore);
  const { user, meData } = storeToRefs(userStore);

  let messengerBooted = false;

  const hideMessenger = (): void => {
    if (!messengerBooted) return;
    shutdown();
    messengerBooted = false;
  };

  const showMessenger = (payload: IntercomBootPayload): void => {
    bootMessenger(payload);
    messengerBooted = true;
  };

  const syncMessenger = (state: IntercomWatchState): void => {
    if (!state.isAuthenticated || state.shouldHideMessenger || !state.email) {
      hideMessenger();
      return;
    }
    showMessenger(buildIntercomPayload(state));
  };

  watch(
    (): IntercomWatchState => ({
      isAuthenticated: isAuthenticated.value,
      shouldHideMessenger: route.meta.requiresAuth === false || isAppEmbeddedInIframe(),
      email: resolveIntercomEmail(meData.value?.person?.email, user.value?.email),
      name: user.value?.name ?? '',
      userId: resolveIntercomUserId(meData.value?.id, user.value?.id),
    }),
    (state) => {
      syncMessenger(state);
    },
    { immediate: true },
  );
}
